/*
 * I started with our old VVVast implementation, updated it for modern ES6,
 * removed any features that weren't used in HTML5, and finally got it working
 *
 * To use:
 *     var vast = new VAST(player, "https://vasturl");
 *     vast.load(function(success, loaded) {
 *         // success = we have ads to play
 *         // loaded = we downloaded the VAST (may not have had ads)
 *     });
 *     var creative = vast.getNextCreative();
 */

import videojs from "video.js";
import xPath from "@utilities/XPath";

const logger = videojs.createLogger("VVVast");

/**
 * Abstracts a VAST
 * @constructor
 */
var VAST = function(vastURI)
{

	// console.log(vastURI);
	var self = this;
	self.ads = [];
	var unLoadedAds = 0;
	var currentAdIndex = 0;
	var currentCreativeIndex = 0;

	self.load = function(callback)
	{
		fetch(vastURI)
			.then(resp => resp.text())
			.then(data =>
			{
				initFromXML(data, callback);
			})
			.catch(err =>
			{
				logger.error(err);
				callback(false, false);
			});
	};

	function initFromXML(xml, callback)
	{
		var ads = xPath(xml, "//Ad");
		var index = 0;
		var nextAd = ads.iterateNext();

		// In some special cases VAST returns 2 Ad tags which breaks the player if this happens during prerolls.
		// To Fix this the following block was changed from a while loop to an if statement, so only the first tag is used.
		// If we ever plan to support multiple ad tags this will have to loop through all the tags.
		if (nextAd)
		{
			unLoadedAds++;
			// Recursive function to burn through <Wrappers> and find the <InLine>
			loadAdFromWrapper(nextAd, index, callback);
			index++;
		}

		if (self.ads.length === 0 && unLoadedAds === 0)
		{
			callback(false, true);
		}
	}

	function loadAdFromWrapper(xml, index, callback)
	{
		var advert = {};

		// Look for <Wrapper> tag
		var wrapper = xPath(xml, "//Wrapper").iterateNext();

		if (wrapper)
		{
			wrapper = xPath(wrapper, "//VASTAdTagURI").iterateNext().textContent;
			// Recursively load from this wrapper
			fetch(wrapper)
				.then(resp => resp.text())
				.then(data =>
				{
					var adTag = xPath(data, "//Ad").iterateNext();
					if (adTag)
					{
						loadAdFromWrapper(adTag, index, callback);
					}

					// Then add our errors, impressions, creatives, etc
					var i = 0;
					var nextValue = getElementValue(xml, "Error", i++);
					while (nextValue !== null)
					{
						self.ads[index].Error.push(nextValue);
						nextValue = getElementValue(xml, "Error", i++);
					}

					i = 0;
					nextValue = getElementValue(xml, "Impression", i++);
					while (nextValue !== null)
					{
						self.ads[index].Impression.push(nextValue);
						nextValue = getElementValue(xml, "Impression", i++);
					}

					var creatives = xPath(xml, "//Creative");
					loadCreatives(creatives, index);
				})
				.catch(err =>
				{
					logger.warn(err);
					callback(false, false);
				});
		}
		else
		{
			// Possible problem: What if it's a Wrapper and there's multiple IDs?
			advert.id = xml.getAttribute("id");
			// Problem: If this is a Wrapper, we will have two or more AdSystems
			// ad.AdSystem = getElementValue( xml, 'AdSystem' );
			// ad.AdSystemVersion = xml.getElementsById('AdSystem')[0].getAttribute('version');

			advert.AdTitle = getElementValue(xml, "AdTitle");
			advert.Description = getElementValue(xml, "Description");
			advert.Survey = getElementValue(xml, "Survey");

			advert.Error = [];
			var i = 0;
			var nextValue = getElementValue(xml, "Error", i++);
			while (nextValue !== null)
			{
				advert.Error.push(nextValue);
				nextValue = getElementValue(xml, "Error", i++);
			}

			advert.Impression = [];
			i = 0;
			nextValue = getElementValue(xml, "Impression", i++);
			while (nextValue !== null)
			{
				advert.Impression.push(nextValue);
				nextValue = getElementValue(xml, "Impression", i++);
			}

			advert.Creatives = [];

			self.ads[index] = advert;

			// Here's where it gets tricksy
			var creatives = xPath(xml, "//Creative");
			self.ads[index].Creatives = [];
			loadCreatives(creatives, index);

			unLoadedAds--;

			// Gotcha: We're firing "onComplete" before stepping back through our
			// recursion to get all tracking events and companion ads
			if (unLoadedAds === 0)
			{
				if (self.ads.length === 0)
				{
					callback(false, true);
				}
				else
				{
					callback(true, true);
				}
			}
		}
	}

	function getElementValue(xml, elementName, count = 0)
	{
		var tmp = xml.getElementsByTagName(elementName)[count];
		return tmp ? tmp.textContent : null;
	}

	function intervalFromString(str)
	{
		if (typeof str !== "string") return null;

		return new Date("1970-01-01T" + str + "Z").getTime() / 1000;
	}

	function loadCreatives(creatives, index)
	{
		var nextCreative = creatives.iterateNext();
		while (nextCreative)
		{
			var creative = {
				ad: self.ads[index],
				id: nextCreative.getAttribute("id"),
				sequence: nextCreative.getAttribute("sequence"),
				TrackingEvents: {}
			};
			var trackingEvents = xPath(nextCreative, "//TrackingEvents/Tracking");
			var trackingEvent = trackingEvents.iterateNext();
			while (trackingEvent)
			{
				var event = trackingEvent.getAttribute("event");
				creative.TrackingEvents[event] = creative.TrackingEvents[event] || [];
				creative.TrackingEvents[event].push(trackingEvent.textContent);

				trackingEvent = trackingEvents.iterateNext();
			}
			// Tricksy...
			nextCreative = xPath(nextCreative, "//Linear | //NonLinearAds | //CompanionAds").iterateNext();
			switch (nextCreative.tagName)
			{
			case "Linear":
				creative.type = "Linear";
				creative.Duration = intervalFromString(getElementValue(nextCreative, "Duration"));
				// creative.AdParameters = '';
				creative.VideoClicks = {
					ClickThrough: getElementValue(nextCreative, "ClickThrough"),
					// Should this be an array?
					ClickTracking: getElementValue(nextCreative, "ClickTracking")
				};
				creative.MediaFiles = [];
				var mediaFiles = xPath(nextCreative, "//MediaFiles/MediaFile");
				var mediaFile = mediaFiles.iterateNext();
				while (mediaFile)
				{
					creative.MediaFiles.push({
						id: mediaFile.getAttribute("id"),
						delivery: mediaFile.getAttribute("delivery"),
						type: mediaFile.getAttribute("type"),
						bitrate: mediaFile.getAttribute("bitrate"),
						width: mediaFile.getAttribute("width"),
						height: mediaFile.getAttribute("height"),
						scalable: mediaFile.getAttribute("scalable") === "true",
						maintainAspectRatio: mediaFile.getAttribute("maintainAspectRatio") === "true",
						// apiFramework: mediaFile.getAttribute('apiFramework'),
						uri: mediaFile.textContent,

						// Not part of VAST spec, but our Master Controller expects it:
						resourceType: "video"
					});
					mediaFile = mediaFiles.iterateNext();
				}
				break;
			case "NonLinearAds":
				creative.type = "NonLinear";
				creative.NonLinears = [];
				break;
			case "CompanionAds":
				creative.type = "Companion";
				creative.Companions = [];
				var Companions = xPath(nextCreative, "//CompanionAds/Companion");
				var Companion = Companions.iterateNext();
				while (Companion)
				{
					var resource = xPath(
						Companion,
						"//StaticResource | //IFrameResource | //HTMLResource"
					).iterateNext();
					creative.Companions.push({
						id: Companion.getAttribute("id"),
						width: Companion.getAttribute("width"),
						height: Companion.getAttribute("height"),
						expandedWidth: Companion.getAttribute("expandedWidth"),
						expandedHeight: Companion.getAttribute("expandedHeight"),
						// apiFramework: Companion.getAttribute('apiFramework'),
						// resource.tagName.substr(0, -8).toLowerCase()
						//    The above would turn "IFrameResource" into "iframe", etc
						resourceType: resource.tagName,
						resource: resource.textContent,
						TrackingEvents: ""
					});

					Companion = Companions.iterateNext();
				}
				break;
			default:
				logger.error("Huh... How'd we get here?");
			}
			if (
			// Is this a valid Linear?
				(creative.type === "Linear" && creative.MediaFiles.length > 0) ||

            // Or a valid NonLinear?
            false ||

            // Or a valid Companion?
            false
			)
			{
				self.ads[index].Creatives.push(creative);
			}

			nextCreative = creatives.iterateNext();
		}
	}

	self.getNextAd = function()
	{
		if (self.ads[currentAdIndex])
		{
			if (self.ads[currentAdIndex].Creatives.length <= currentCreativeIndex)
			{
				currentAdIndex++;
				currentCreativeIndex = 0;
				return self.getNextAd();
			}
			return self.ads[currentAdIndex];
		}
		else
		{
			return null;
		}
	};

	self.getNextCreative = function()
	{
		return self.getCreativeForAd(self.getNextAd());
	};

	self.getCreativeForAd = function(advert)
	{
		if (advert && advert.Creatives[currentCreativeIndex])
		{
			return advert.Creatives[currentCreativeIndex++];
		}
		else
		{
			return null;
		}
	};
};

export default VAST;
